import { type FC, type ReactNode } from 'react'
import { Form } from 'react-bootstrap'
import styles from './CalendarInvitePreview.module.scss'
import { type BookingInvitee, type Location, MeetingMode, type SchedulingEventType } from '../../../models/Scheduling'
import { type LocationInput } from '../../../store/scheduling/dto'
import { type BookingSlot } from './form/timeSlots'
import { clientDisplayName } from '../shared/schedulingDisplay'
import { formatInviteWhen } from '../bookingFormat'

interface CalendarInvitePreviewProps {
  eventType: SchedulingEventType
  ownerName: string
  invitees: BookingInvitee[]
  meetingMode: MeetingMode
  slot: BookingSlot | null
  title: string
  timezoneLabel: string
  location: Location | LocationInput | null
  note: string
  onNoteChange: (note: string) => void
}

// Renders into the parent's two-column grid (label cell + value cell)
const PreviewRow: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
  <>
    <div className='paragraph-small zocks-text-gray-500'>{label}</div>
    <div className='paragraph-small'>{children}</div>
  </>
)

const ZoomPill: FC = () => (
  <span className='d-inline-flex align-items-center zocks-gap-0.25 badge zocks-bg-color-gray-100 zocks-text-gray-900'>
    <i className='bi bi-camera-video' aria-hidden='true' />
    Zoom
  </span>
)

// Right column for the "Propose a specific time" mode: the calendar invite the client receives.
export const CalendarInvitePreview: FC<CalendarInvitePreviewProps> = ({
  eventType,
  ownerName,
  invitees,
  meetingMode,
  slot,
  title,
  timezoneLabel,
  location,
  note,
  onNoteChange,
}) => {
  const inviteeNames = invitees.map((invitee) => clientDisplayName({ firstName: invitee.firstName, lastName: invitee.lastName, email: invitee.email }))
  const withWho = [ownerName, ...inviteeNames].filter(Boolean).join(', ')
  const whenText = slot ? `${formatInviteWhen(slot.startAt)} · ${timezoneLabel || 'Set timezone'}` : 'Pick a time'
  const locationText = location && location.address ? `${location.address}${location.note ? ` · ${location.note}` : ''}` : ''

  return (
    <div className='d-flex flex-column zocks-gap-0.5'>
      <h5 className='mb-0'>Calendar invite</h5>
      <div className='paragraph-small zocks-text-gray-600'>This event gets sent to the client and gets added to the selected user&apos;s calendar.</div>
      <div className='d-flex flex-column gap-3 p-3 mt-2 border zocks-border-color-gray-200 zocks-border-1.0 zocks-bg-color-white'>
        {title ? <div className='bold'>{title}</div> : null}
        <div className={styles.rows}>
          <PreviewRow label='Meeting'>
            {eventType.name} · {eventType.durationMinutes} min
          </PreviewRow>
          <PreviewRow label='When'>{whenText}</PreviewRow>
          <PreviewRow label='Where'>
            {meetingMode === MeetingMode.IN_PERSON ? locationText ? locationText : <span className='zocks-text-gray-500'>Set location</span> : <ZoomPill />}
          </PreviewRow>
          <PreviewRow label='With'>{withWho}</PreviewRow>
        </div>
        <hr className='my-0 zocks-text-gray-300' />
        <div className='d-flex flex-column zocks-gap-0.25'>
          <label className='bold' htmlFor='schedule-meeting-note'>
            Note to client
          </label>
          <Form.Control
            as='textarea'
            id='schedule-meeting-note'
            rows={4}
            value={note}
            placeholder='You can reschedule or cancel this meeting here: [Link]'
            onChange={(event) => onNoteChange(event.currentTarget.value)}
          />
        </div>
      </div>
    </div>
  )
}
